/**
 * Console Admin global — gestion des madrassas (création, validation des
 * demandes pendantes, vue d'ensemble) + email logs.
 *
 * Réservé aux utilisateurs `role === 'admin'`.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Plus, CheckCircle2, XCircle, Mail, RefreshCcw,
} from 'lucide-react';
import { GlassCard, SectionHeader } from '../components/shared';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { madrassaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUS_CLS = {
  pending:  'bg-amber-500/15 text-amber-300 border-amber-500/30',
  active:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  rejected: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  archived: 'bg-surface-elevated text-text-muted border-border',
};

const NewMadrassaDialog = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!name.trim()) { setError('Nom requis.'); return; }
    setError(null);
    try {
      await madrassaApi.adminCreate({
        name: name.trim(),
        description: description.trim() || null,
        admin_email: adminEmail.trim() || null,
      });
      setOpen(false);
      setName(''); setDescription(''); setAdminEmail('');
      onDone && onDone();
    } catch (e) { setError(e?.response?.data?.detail || 'Erreur.'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="admin-madrassa-new-open">
          <Plus className="w-3.5 h-3.5 mr-1" />
          Nouvelle madrassa
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="admin-madrassa-new-dialog">
        <DialogHeader>
          <DialogTitle>Créer une madrassa active</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <Input
            placeholder="Nom"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="admin-madrassa-new-name"
          />
          <Textarea
            placeholder="Description (optionnel)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            data-testid="admin-madrassa-new-description"
          />
          <Input
            type="email"
            placeholder="Email du futur admin de madrassa (optionnel)"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            data-testid="admin-madrassa-new-admin-email"
          />
          {error && <p className="text-sm text-rose-300">{error}</p>}
          <div className="flex justify-end">
            <Button onClick={submit} data-testid="admin-madrassa-new-submit">Créer</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MadrassaAdminPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [a, l] = await Promise.all([
        madrassaApi.adminList().catch(() => ({ data: { items: [] } })),
        madrassaApi.adminEmailLogs().catch(() => ({ data: { items: [] } })),
      ]);
      setItems(a.data?.items || []);
      setLogs(l.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const approve = async (id) => {
    try { await madrassaApi.adminApprove(id); refresh(); }
    catch { /* surface a toast */ }
  };
  const reject = async (id) => {
    try { await madrassaApi.adminReject(id); refresh(); }
    catch { /* ignore */ }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer cette madrassa ? Si elle a du contenu, elle sera archivée.')) return;
    try {
      const r = await madrassaApi.delete(id);
      if (r.data?.status === 'archived' && window.confirm(
        `${r.data?.message}\n\nForcer la suppression définitive ?`,
      )) {
        await madrassaApi.delete(id, true);
      }
      refresh();
    } catch { /* ignore */ }
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="text-sm text-text-muted">Réservé aux administrateurs globaux.</p>
        <Button variant="ghost" size="sm" onClick={() => navigate('/')}>Retour</Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="madrassa-admin-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/madrassa')} className="mb-3"
              data-testid="madrassa-admin-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour
      </Button>
      <SectionHeader
        overline="Console admin global"
        title="Gestion des madrassas"
        subtitle="Validez les demandes de création et suivez les notifications email."
      />

      <div className="mt-6 flex flex-wrap gap-2">
        <NewMadrassaDialog onDone={refresh} />
        <Button variant="ghost" size="sm" onClick={refresh} data-testid="madrassa-admin-refresh">
          <RefreshCcw className="w-3 h-3 mr-1" /> Rafraîchir
        </Button>
      </div>

      <Tabs defaultValue="all" className="mt-6">
        <TabsList className="bg-surface border border-border">
          <TabsTrigger value="all" data-testid="madrassa-admin-tab-all">Toutes ({items.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="madrassa-admin-tab-pending">
            En attente ({items.filter((m) => m.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="email-logs" data-testid="madrassa-admin-tab-email-logs">
            <Mail className="w-3.5 h-3.5 mr-1" />Logs email
          </TabsTrigger>
        </TabsList>
        <TabsContent value="all" className="mt-4 space-y-2">
          {loading && <p className="text-sm text-text-muted">Chargement…</p>}
          {items.map((m) => (
            <MadrassaAdminRow key={m.id} m={m}
              onApprove={approve} onReject={reject} onDelete={remove} />
          ))}
        </TabsContent>
        <TabsContent value="pending" className="mt-4 space-y-2">
          {items.filter((m) => m.status === 'pending').map((m) => (
            <MadrassaAdminRow key={m.id} m={m}
              onApprove={approve} onReject={reject} onDelete={remove} />
          ))}
        </TabsContent>
        <TabsContent value="email-logs" className="mt-4 space-y-2">
          {logs.length === 0 && (
            <p className="text-sm text-text-muted" data-testid="madrassa-admin-email-logs-empty">
              Aucun email enregistré.
            </p>
          )}
          {logs.map((e) => (
            <GlassCard key={e.id} data-testid={`madrassa-admin-email-${e.id}`}>
              <div className="flex items-start justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <p className="text-sm text-text-primary truncate">{e.subject}</p>
                  <p className="text-[11px] text-text-muted">{e.to} · {e.type}</p>
                </div>
                <Badge variant="outline" className="text-[10px]">{e.status}</Badge>
              </div>
              <p className="text-xs text-text-muted mt-2">{e.body}</p>
              <p className="text-[10px] text-text-muted mt-2">{e.created_at}</p>
            </GlassCard>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
};

const MadrassaAdminRow = ({ m, onApprove, onReject, onDelete }) => (
  <GlassCard data-testid={`madrassa-admin-row-${m.id}`}>
    <div className="flex items-center gap-3 flex-wrap">
      <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center shrink-0">
        <Building2 className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-text-primary truncate">{m.name}</span>
          <Badge className={`${STATUS_CLS[m.status] || 'border-border'} border text-[10px]`}>
            {m.status}
          </Badge>
        </div>
        {m.description && (
          <p className="text-xs text-text-muted truncate mt-1">{m.description}</p>
        )}
        <p className="text-[11px] text-text-muted mt-1">
          Slug: {m.slug} · Créé: {m.created_at?.slice(0, 10)}
        </p>
      </div>
      {m.status === 'pending' && (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onReject(m.id)}
                  data-testid={`madrassa-admin-row-${m.id}-reject`}>
            <XCircle className="w-3.5 h-3.5 mr-1" /> Rejeter
          </Button>
          <Button size="sm" onClick={() => onApprove(m.id)}
                  data-testid={`madrassa-admin-row-${m.id}-approve`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver
          </Button>
        </div>
      )}
      {onDelete && (
        <Button size="sm" variant="ghost" onClick={() => onDelete(m.id)}
                data-testid={`madrassa-admin-row-${m.id}-delete`}>
          <XCircle className="w-3.5 h-3.5 text-rose-300" />
        </Button>
      )}
    </div>
  </GlassCard>
);

export default MadrassaAdminPage;
